import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Polygon, useMap } from "react-leaflet";
import L from "leaflet";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck, MessagesSquare, Bell, BellOff, Loader2, Clock, CheckCircle2, XCircle, ThumbsUp, ThumbsDown, Camera, X, History, Edit3, Send } from "lucide-react";
import { CommentLikeButton, CommentSortSelect } from "@/components/CommentSocial";
import { ReportContentButton } from "@/components/ReportContentDialog";
import VideoGallery from "@/components/VideoGallery";
import ShareButton from "@/components/ShareButton";
import { displayAuthorName } from "@/lib/anonAuthor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function FitToPolygon({ coords }) {
  const map = useMap();
  useEffect(() => {
    if (coords && coords.length >= 3) {
      const latlngs = coords.map(([lng, lat]) => [lat, lng]);
      const b = L.latLngBounds(latlngs);
      map.fitBounds(b.pad(0.1), { animate: true, duration: 0.6 });
    }
  }, [coords, map]);
  return null;
}

export default function EventDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [ev, setEv] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [newUpdateText, setNewUpdateText] = useState("");
  const [uploadingUpdate, setUploadingUpdate] = useState(false);
  const [updatePhotos, setUpdatePhotos] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [concludeOpen, setConcludeOpen] = useState(false);
  const [concludeNote, setConcludeNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [commentSort, setCommentSort] = useState(() => {
    try { return localStorage.getItem("tc_event_comment_sort") || "recent"; } catch { return "recent"; }
  });

  const load = async () => {
    try {
      const [{ data }, { data: cd }] = await Promise.all([
        api.get(`/events/${id}`),
        api.get(`/events/${id}/comments`, { params: { sort: commentSort } }),
      ]);
      setEv(data);
      setComments(cd.comments || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, [id, commentSort]);
  useEffect(() => { try { localStorage.setItem("tc_event_comment_sort", commentSort); } catch {} }, [commentSort]);

  const isManager = user && (user.role === "admin" || user.role === "moderator" || user.verified_entity);
  const isAdmin = user && (user.role === "admin" || user.role === "moderator");
  const isFollowing = user && ev && (ev.followers || []).includes(user.user_id);

  const coords = ((ev?.geometry?.coordinates) || [[]])[0];
  const polygonLatLng = useMemo(() => coords.map(([lng, lat]) => [lat, lng]), [coords]);

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${id}/approve`, {});
      if (data.ok) { toast.success("Evento pubblicato"); await load(); }
      else toast.error(data.message || "Errore");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const submitReject = async () => {
    if (!rejectReason.trim()) return toast.error("Motivazione obbligatoria");
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${id}/reject`, { reason: rejectReason.trim() });
      if (data.ok) { toast.success("Evento rifiutato"); setRejectOpen(false); setRejectReason(""); await load(); }
      else toast.error(data.message || "Errore");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const submitConclude = async () => {
    setBusy(true);
    try {
      await api.post(`/events/${id}/conclude`, { note: concludeNote.trim() || null });
      toast.success("Evento concluso");
      setConcludeOpen(false); setConcludeNote("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const toggleFollow = async () => {
    if (!user) return nav("/auth?mode=signin");
    try {
      const { data } = await api.post(`/events/${id}/follow`, {});
      toast.success(data.following ? "Segui l'evento" : "Hai smesso di seguire l'evento");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const submitComment = async () => {
    if (!user) return nav("/auth?mode=signin");
    if (!newComment.trim()) return;
    try {
      await api.post(`/events/${id}/comments`, { text: newComment.trim() });
      setNewComment("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const uploadUpdatePhoto = async (file) => {
    if (!file) return;
    setUploadingUpdate(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpdatePhotos((p) => [...p, data.path]);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload fallito"); }
    finally { setUploadingUpdate(false); }
  };
  const submitUpdate = async () => {
    if (!newUpdateText.trim() && updatePhotos.length === 0) return toast.error("Testo o foto richiesti");
    try {
      const { data } = await api.post(`/events/${id}/updates`, { text: newUpdateText.trim(), photos: updatePhotos });
      toast.success(data.update?.status === "published" ? "Aggiornamento pubblicato" : "Aggiornamento inviato alla revisione");
      setNewUpdateText(""); setUpdatePhotos([]);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const moderateUpdate = async (upd_id, status) => {
    try {
      const { data } = await api.patch(`/events/${id}/updates/${upd_id}`, { status });
      if (data.ok) { toast.success(status === "published" ? "Aggiornamento pubblicato" : "Aggiornamento rifiutato"); await load(); }
      else toast.error(data.message || "Errore");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  if (!ev) return (
    <div className="min-h-screen bg-[#F7F5F0]"><AppHeader /><div className="max-w-4xl mx-auto px-6 py-16 text-slate-500">Caricamento…</div></div>
  );

  const stateBadge = {
    published: { c: "bg-red-100 text-red-800", l: "In corso" },
    pending_moderation: { c: "bg-amber-100 text-amber-800", l: "In attesa di verifica" },
    rejected: { c: "bg-slate-200 text-slate-700", l: "Rifiutato" },
    concluded: { c: "bg-emerald-100 text-emerald-800", l: "Concluso" },
  }[ev.state] || { c: "bg-slate-100 text-slate-700", l: ev.state };

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="event-detail-page">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-6 pb-4 flex flex-wrap items-center justify-between gap-3">
        <BackButton testid="event-detail-back" />
        <div className="flex gap-2 flex-wrap">
          {user && ev.state === "published" && (
            <Button size="sm" variant={isFollowing ? "default" : "outline"} onClick={toggleFollow} data-testid="event-follow-btn" className={`rounded-full ${isFollowing ? "bg-red-600 hover:bg-red-700 text-white" : ""}`}>
              {isFollowing ? <><BellOff size={13} className="mr-1" /> Non seguire</> : <><Bell size={13} className="mr-1" /> Segui</>}
            </Button>
          )}
          {isManager && ev.state !== "concluded" && ev.state !== "rejected" && (
            <Button size="sm" variant="outline" onClick={() => nav(`/events/${id}/edit`)} data-testid="event-edit-btn" className="rounded-full">
              <Edit3 size={13} className="mr-1" /> Modifica
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-8 space-y-6">
        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <Badge className={stateBadge.c} data-testid="event-state">{stateBadge.l}</Badge>
              <Badge variant="outline" data-testid="event-kind-badge">{t(`event_kind_${ev.kind}`) || ev.kind}</Badge>
              {ev.author_verified_entity && (
                <Badge className="bg-blue-100 text-blue-800" data-testid="event-official-badge">
                  <ShieldCheck size={11} className="mr-1" /> Ente verificato
                </Badge>
              )}
              {ev.province_name && <Badge variant="outline">{ev.province_name} · {ev.province_country}</Badge>}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900" data-testid="event-title-h1">{ev.title}</h1>
            <div className="mt-3 text-sm text-slate-700 whitespace-pre-line" data-testid="event-description">{ev.description}</div>
            <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-3">
              <span>Proposto da {displayAuthorName(ev, t)}</span>
              <span>· Creato {new Date(ev.created_at).toLocaleString()}</span>
              {ev.published_at && <span>· Pubblicato {new Date(ev.published_at).toLocaleString()}</span>}
              {ev.concluded_at && <span>· Concluso {new Date(ev.concluded_at).toLocaleString()}</span>}
              {ev.area_km2 != null && <span>· Area ~{ev.area_km2} km²</span>}
            </div>
            {/* Phase 5 — share only when the event is publicly viewable */}
            {ev.state === "published" && (
              <div className="mt-3">
                <ShareButton
                  path={`/events/${id}`}
                  title={ev.title}
                  description={(ev.description || "").slice(0, 200)}
                  testid="event-share"
                />
              </div>
            )}
          </div>
          {ev.photos && ev.photos.length > 0 && (
            <div className="grid grid-cols-2 gap-1 max-h-[300px]">
              {ev.photos.slice(0, 4).map((p, i) => (
                <img key={i} src={`${BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-40 object-cover" data-testid={`event-photo-${i}`} />
              ))}
            </div>
          )}
          {ev.videos && ev.videos.length > 0 && (
            <div className="p-3 border-t border-slate-100">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{t("video_upload_label")}</div>
              <VideoGallery paths={ev.videos} testidPrefix="event-videos" />
            </div>
          )}
        </div>

        {/* Cosa fare ora */}
        {ev.what_to_do && (
          <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-5" data-testid="event-what-to-do">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center shrink-0"><AlertTriangle size={18} /></div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Cosa fare ora</div>
                <div className="text-sm text-red-900 mt-1 whitespace-pre-line">{ev.what_to_do}</div>
              </div>
            </div>
          </div>
        )}

        {/* Admin moderation for pending */}
        {isAdmin && ev.state === "pending_moderation" && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5" data-testid="event-mod-block">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0"><ShieldCheck size={18} /></div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold">Moderazione admin</div>
                <div className="font-heading font-bold text-slate-900 text-base mt-0.5">Questo evento è in attesa di verifica</div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <Button onClick={approve} disabled={busy} data-testid="event-approve-btn" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white">
                    <ThumbsUp size={14} className="mr-1.5" /> Approva
                  </Button>
                  <Button onClick={() => setRejectOpen(true)} disabled={busy} variant="outline" data-testid="event-reject-btn" className="rounded-full border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800">
                    <ThumbsDown size={14} className="mr-1.5" /> Rifiuta
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Manager actions on published event */}
        {isManager && ev.state === "published" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap gap-2" data-testid="event-manager-actions">
            <Button onClick={() => nav(`/events/${id}/area`)} variant="outline" data-testid="event-area-btn" className="rounded-full"><Edit3 size={13} className="mr-1" /> Modifica area</Button>
            <Button onClick={() => setConcludeOpen(true)} variant="outline" data-testid="event-conclude-btn" className="rounded-full text-emerald-700 border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800"><CheckCircle2 size={13} className="mr-1" /> Dichiara concluso</Button>
          </div>
        )}

        {/* Map with polygon */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="flex items-center justify-between p-3 border-b border-slate-100">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Area coinvolta</div>
            <Button size="sm" variant="ghost" onClick={() => setHistoryOpen(true)} data-testid="event-area-history-btn" className="rounded-full text-xs">
              <History size={12} className="mr-1" /> Storico area ({(ev.area_versions || []).length})
            </Button>
          </div>
          <div className="h-[380px]" data-testid="event-map">
            <MapContainer center={ev.centroid ? [ev.centroid.lat, ev.centroid.lng] : [42, 12]} zoom={10} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
              {polygonLatLng.length >= 3 && (
                <>
                  <Polygon positions={polygonLatLng} pathOptions={{ color: "#EF4444", weight: 2, fillColor: "#EF4444", fillOpacity: 0.18 }} />
                  <FitToPolygon coords={coords} />
                </>
              )}
            </MapContainer>
          </div>
        </div>

        {/* Verified updates section */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="event-updates-block">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-lg font-bold text-slate-900">Aggiornamenti verificati</div>
          </div>
          <div className="space-y-3 mb-4">
            {(ev.updates || []).length === 0 && (
              <div className="text-sm text-slate-500 italic">Ancora nessun aggiornamento.</div>
            )}
            {(ev.updates || []).slice().reverse().map((u) => (
              <div key={u.update_id} className={`border rounded-xl p-3 ${u.is_official ? "border-blue-200 bg-blue-50" : "border-slate-200 bg-white"}`} data-testid={`event-update-${u.update_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                    {u.is_official ? <Badge className="bg-blue-600 text-white">Ufficiale</Badge> : <Badge variant="outline">Utente</Badge>}
                    {u.status === "pending_moderation" && <Badge className="bg-amber-100 text-amber-800">In attesa</Badge>}
                    <span>{displayAuthorName(u, t)}</span>
                    <span className="text-[10px] text-slate-500">{new Date(u.created_at).toLocaleString()}</span>
                  </div>
                  {isAdmin && u.status === "pending_moderation" && (
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => moderateUpdate(u.update_id, "published")} data-testid={`event-update-approve-${u.update_id}`} className="rounded-full h-7 bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white text-xs">Approva</Button>
                      <Button size="sm" variant="outline" onClick={() => moderateUpdate(u.update_id, "rejected")} data-testid={`event-update-reject-${u.update_id}`} className="rounded-full h-7 text-xs border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800">Rifiuta</Button>
                    </div>
                  )}
                </div>
                <div className="mt-2 text-sm text-slate-800 whitespace-pre-line">{u.text}</div>
                {(u.photos || []).length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-1">
                    {u.photos.map((p, i) => (
                      <img key={i} src={`${BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-20 object-cover rounded-md" />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          {user && ev.state === "published" && (
            <div className="border-t border-slate-100 pt-3 space-y-2">
              <Textarea
                data-testid="event-new-update"
                rows={2}
                value={newUpdateText}
                onChange={(e) => setNewUpdateText(e.target.value)}
                placeholder={isManager ? "Aggiornamento ufficiale…" : "Fornisci informazioni dal territorio (verrà rivista da un Admin)…"}
                className="rounded-xl"
              />
              <div className="flex flex-wrap gap-2 items-center">
                <div className="flex gap-1 flex-wrap">
                  {updatePhotos.map((p, i) => (
                    <div key={i} className="relative w-14 h-14 rounded-md overflow-hidden border border-slate-200">
                      <img src={`${BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setUpdatePhotos((ps) => ps.filter((_, j) => j !== i))} className="absolute top-0 right-0 w-5 h-5 rounded-bl-md bg-black/60 text-white flex items-center justify-center"><X size={10} /></button>
                    </div>
                  ))}
                </div>
                <label className="h-9 px-3 rounded-full border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1 cursor-pointer" data-testid="event-update-add-photo">
                  {uploadingUpdate ? <Loader2 className="animate-spin" size={13} /> : <Camera size={13} />} Foto
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadUpdatePhoto(f); e.target.value = ""; }} />
                </label>
                <Button data-testid="event-update-submit" onClick={submitUpdate} className="ml-auto rounded-full bg-blue-600 hover:bg-blue-700 text-white hover:text-white h-9 text-xs">
                  <Send size={13} className="mr-1" /> {isManager ? t("publish") : t("submit_for_review")}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Comments section */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="event-comments-block">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="font-heading text-lg font-bold text-slate-900">{t("coordinate") || "Commenti"}</div>
            <div className="ml-auto">
              <CommentSortSelect value={commentSort} onChange={setCommentSort} testid="event-comment-sort" />
            </div>
          </div>
          <div className="space-y-2 mb-3">
            {comments.length === 0 && <div className="text-sm text-slate-500 italic">{t("no_comments") || "Nessun commento."}</div>}
            {comments.map((c) => {
              const isOwn = user && user.user_id === c.author_id;
              return (
                <div key={c.comment_id} className="border-b border-slate-100 pb-2 group" data-testid={`event-comment-${c.comment_id}`}>
                  <div className="text-xs font-semibold text-slate-900 flex items-center gap-2">
                    <span>{displayAuthorName(c, t)}</span>
                    <span className="text-[10px] text-slate-500 font-normal">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-slate-700 mt-0.5 whitespace-pre-line">{c.text}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <CommentLikeButton
                      targetKind="event_comment"
                      targetId={c.comment_id}
                      initialCount={c.like_count || 0}
                      initialLiked={!!c.liked_by_me}
                      testid={`event-comment-like-${c.comment_id}`}
                    />
                    {!isOwn && user && (
                      <ReportContentButton
                        targetType="event_comment"
                        targetId={c.comment_id}
                        label=""
                        testid={`event-comment-report-${c.comment_id}`}
                        className="h-6 px-2 text-[11px] opacity-0 group-hover:opacity-100"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {user && (
            <div className="flex gap-2 items-center border-t border-slate-100 pt-3">
              <Textarea data-testid="event-comment-input" rows={2} value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Scrivi un commento…" className="rounded-xl flex-1" />
              <Button onClick={submitComment} data-testid="event-comment-submit" className="rounded-full h-11 bg-slate-800 hover:bg-slate-900 text-white hover:text-white"><MessagesSquare size={14} className="mr-1" /> Invia</Button>
            </div>
          )}
        </div>
      </div>

      {/* Area history dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg" data-testid="event-area-history-dialog">
          <DialogHeader><DialogTitle>Evoluzione dell'area</DialogTitle></DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {(ev.area_versions || []).slice().reverse().map((v) => (
              <div key={v.version_id} className="border border-slate-200 rounded-lg p-2 text-xs" data-testid={`event-area-version-${v.version_id}`}>
                <div className="font-semibold text-slate-800">{new Date(v.updated_at).toLocaleString()}</div>
                <div className="text-slate-600">Autore: {v.updated_by_name}</div>
                {v.note && <div className="text-slate-700 mt-1">Nota: {v.note}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="event-reject-dialog">
          <DialogHeader><DialogTitle>{t("admin_reject_dialog_title")}</DialogTitle></DialogHeader>
          <Textarea data-testid="event-reject-reason" rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t("admin_reason_required")} className="rounded-xl" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setRejectOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submitReject} disabled={busy || !rejectReason.trim()} data-testid="event-reject-submit" className="rounded-full bg-red-600 hover:bg-red-700 text-white hover:text-white">{t("confirm_reject")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conclude dialog */}
      <Dialog open={concludeOpen} onOpenChange={setConcludeOpen}>
        <DialogContent data-testid="event-conclude-dialog">
          <DialogHeader><DialogTitle>{t("admin_declare_event_ended")}</DialogTitle></DialogHeader>
          <Textarea data-testid="event-conclude-note" rows={3} value={concludeNote} onChange={(e) => setConcludeNote(e.target.value)} placeholder={t("optional_note_followers")} className="rounded-xl" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConcludeOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submitConclude} disabled={busy} data-testid="event-conclude-submit" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white">{t("confirm_conclusion")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
